import type { ComputedRef, Ref } from 'vue';

interface ConsentPreferences {
    analyticsStorage: boolean;
    adStorage: boolean;
    adPersonalization: boolean;
    adUserData: boolean;
}

interface UseConsent {
    hasResponded: ComputedRef<boolean>;
    consentPreferences: ComputedRef<ConsentPreferences>;
    setConsent: (preferences: ConsentPreferences) => void;
    preferences: Ref<undefined | HTMLDialogElement>;
}

const defaultPreferences: ConsentPreferences = {
    analyticsStorage: true,
    adStorage: true,
    adPersonalization: true,
    adUserData: true
};

export default function useConsent(): UseConsent {
    const cookieConsent = useCookie<ConsentPreferences | undefined>('cookie-consent', {
        default: () => undefined,
        sameSite: 'strict',
        secure: true,
        httpOnly: false,
        maxAge: 60 * 60 * 24 * 30 * 6, // 6 months default
        // todo - remove legacy support after 6 months
        encode: (value: ConsentPreferences) => JSON.stringify(value),
        decode: (value: string) => {
            // Handle legacy boolean values for backward compatibility
            if (value === 'true') {
                return {
                    analyticsStorage: true,
                    adStorage: true,
                    adPersonalization: true,
                    adUserData: true
                };
            }
            if (value === 'false') {
                return {
                    analyticsStorage: false,
                    adStorage: false,
                    adPersonalization: false,
                    adUserData: false
                };
            }

            // Handle new JSON format
            try {
                const parsed = JSON.parse(value);
                return {
                    analyticsStorage: parsed.analyticsStorage ?? false,
                    adStorage: parsed.adStorage ?? false,
                    adPersonalization: parsed.adPersonalization ?? false,
                    adUserData: parsed.adUserData ?? false
                };
            } catch {
                return defaultPreferences;
            }
        }
    });

    const hasResponded = computed(() => import.meta.client && !!cookieConsent.value);
    const consentPreferences = computed(
        () => hasResponded.value && cookieConsent.value ? cookieConsent.value : defaultPreferences
    );
    const preferences = useState<undefined | HTMLDialogElement>('preferences', () => undefined);
    const { gtag, initialize, enableAnalytics, disableAnalytics } = useGtag();

    const updateGtagConsent = (prefs: ConsentPreferences) => {
        initialize();

        /* eslint-disable @typescript-eslint/naming-convention */
        gtag('consent', 'update', {
            ad_user_data: prefs.adUserData ? 'granted' : 'denied',
            ad_personalization: prefs.adPersonalization ? 'granted' : 'denied',
            ad_storage: prefs.adStorage ? 'granted' : 'denied',
            analytics_storage: prefs.analyticsStorage ? 'granted' : 'denied'
        });
        /* eslint-enable @typescript-eslint/naming-convention */

        // Enable/disable analytics based on analytics storage consent
        if (prefs.analyticsStorage) {
            enableAnalytics();
        } else {
            disableAnalytics();
        }
    };

    // Initialize gtag based on existing consent if user has already hasResponded
    if (import.meta.client && hasResponded.value) {
        updateGtagConsent(consentPreferences.value);
    }

    const setConsent = (preferences: ConsentPreferences) => {
        cookieConsent.value = preferences;
        updateGtagConsent(preferences);
    };

    return {
        hasResponded,
        consentPreferences,
        setConsent,
        preferences
    };
}
